import * as React from 'react';
import { RootState } from '../reducers/reducers';
import { connect, ConnectedProps } from 'react-redux';
import { ReleaseStatus, ReleaseStepKickoff, ReleaseTimelinePhase } from '../types';
import { getReleaseStepFromState, getIsLoading, getUpdated, getError } from '../helpers';
import styles from './styles/stepStyles';
import { FeatureSection } from '../typescriptHelpers';
import { ReleaseStepInactiveContext } from '../contexts';
import { displayUserNameLong } from '../helpers/displayHelpers';
import Text from 'antd/lib/typography/Text';
import Layout, { Content, Footer } from 'antd/lib/layout/layout';
import Space from 'antd/lib/space';
import { antGrey, runwayColors } from './styles/globalStyles';
import PlanningSummaryModal from './PlanningSummaryModal';
import UserAvatar from './UserAvatar';
import { TRY_DELETE_RELEASE_ENTITY } from '../actions/release';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../helpers/aclHelpers';
import {
    selectRelease,
    selectApp,
    selectOrg,
    selectReleasePilotForRelease,
    selectIsWorkflowsSSRStepsEnabled,
} from '../selectors/selectors';
import { ReleaseEventsTimelineSection } from './releaseOverview/ReleaseTimelineSection';
import { ReleaseTimelineSectionContext } from './timelineEvents/EventsTimeline';
import KickoffInfo from './kickoff/KickoffInfo';
import ReleaseActionsComponent from './sharedComponents/ReleaseActionsComponent';
import { EditOutlined } from '@ant-design/icons';
import { EmptyStateWrapper } from './releaseEmptyState/EmptyStateWrapper';
import KickoffStepFooter from './KickoffStepFooter';
import Skeleton from 'antd/lib/skeleton/Skeleton';
import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';
import MarkdownParagraphWithEllipsis from './HOCs/MarkdownParagraphWithEllipsis';

const moduleTitleStyle: React.CSSProperties = {
    fontSize: 13,
};

interface State {
    editReleaseModalVisible: boolean;
    versionSummaryModalVisible: boolean;
}

class StepContentKickOff extends React.Component<Props, State> {
    constructor(props) {
        super(props);

        this.state = {
            editReleaseModalVisible: false,
            versionSummaryModalVisible: false,
        };
    }

    render() {
        const cardStyle: React.CSSProperties = {
            backgroundColor: antGrey.grey3,
            borderRadius: 6,
            paddingTop: 16,
            paddingBottom: 16,
            paddingLeft: 24,
            paddingRight: 24,
            flexGrow: 1,
        };

        const cardContainerStyle: React.CSSProperties = {
            flexShrink: 0,
            display: 'flex',
            minWidth: 300,
        };

        const contentStyle: React.CSSProperties = this.props.showFooter ? { ...styles.contentWithFooterStyle } : {};

        return (
            <Layout>
                <Content
                    style={{
                        ...styles.innerContent,
                        ...contentStyle,
                        display: 'flex',
                        flexDirection: 'column',
                        paddingTop: 0,
                    }}
                >
                    <Space direction="vertical" size="large">
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Space direction="vertical" size={16} style={{ paddingTop: 28 }}>
                                <Space direction="vertical">
                                    <Text strong style={{ ...styles.stepContentStatusText }}>
                                        Version {this.props.displayableVersion}
                                        {!!this.props.releaseName && this.props.releaseName != '' ?
                                            ' - "' + this.props.releaseName + '"'
                                        :   ''}
                                    </Text>
                                    <Text type="secondary">
                                        {kickoffHelperText(
                                            this.props.isStaticReleaseBranch,
                                            this.props.isTrunkBased,
                                            this.props.releaseBranch,
                                            this.props.workingBranch,
                                        )}
                                    </Text>
                                </Space>
                                {!this.props.isWorkflowsSSRStepsEnabled && (
                                    <ReleaseActionsComponent
                                        orgId={this.props.orgId}
                                        appId={this.props.appId}
                                        releaseId={this.props.releaseId}
                                        version={this.props.version}
                                        releaseType={this.props.releaseType}
                                        editReleaseDisabled={this.props.editDeleteReleaseDisabled}
                                        deleteReleaseDisabled={this.props.editDeleteReleaseDisabled}
                                        skipReleaseDisabled={this.props.skipReleaseDisabled}
                                        rollbackTargetVersionInfo={this.props.rollbackTargetVersionInfo}
                                        canBeSubmitted={this.props.canBeSubmitted}
                                        canRevertCompletedStatus={this.props.canRevertCompletedStatus}
                                        releaseStatus={this.props.releaseStatus}
                                    />
                                )}
                            </Space>
                            <ReleaseEventsTimelineSection
                                orgId={this.props.orgId}
                                appId={this.props.appId}
                                releaseId={this.props.releaseId}
                                stepRunId={this.props.stepRunComponentProps?.stepRunId}
                                context={ReleaseTimelineSectionContext.ReleaseStep}
                                releaseStepType={this.props.releaseStepType}
                            />
                        </div>
                        <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'start' }}>
                            <KickoffInfo
                                {...this.props.data}
                                releaseType={this.props.releaseType}
                                orgId={this.props.orgId}
                                appId={this.props.appId}
                                releaseId={this.props.releaseId}
                                releaseStepType={this.props.releaseStepType}
                            />
                            <div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
                                <div style={{ ...cardContainerStyle, marginBottom: 24 }}>
                                    <EmptyStateWrapper
                                        releaseStepType={this.props.releaseStepType}
                                        appId={this.props.appId}
                                        featureSections={[FeatureSection.KickoffReleasePilot]}
                                        releaseId={this.props.releaseId}
                                    >
                                        {(isInEmptyState) => (
                                            <div style={{ ...cardStyle }}>
                                                <Space direction="vertical" size="large">
                                                    <Text style={{ ...moduleTitleStyle }} type="secondary">
                                                        Release pilot
                                                    </Text>
                                                    {(this.props.releasePilot || this.props.releasePilotId) && (
                                                        <Space size={16}>
                                                            <UserAvatar
                                                                imageUrl={
                                                                    this.props.releasePilot?.commsAvatars?.image48
                                                                }
                                                            ></UserAvatar>
                                                            <Space direction="vertical" size={0}>
                                                                {this.props.releasePilot?.firstName ?
                                                                    <Text>
                                                                        {displayUserNameLong(this.props.releasePilot)}
                                                                    </Text>
                                                                :   <Text>{this.props.releasePilotId}</Text>}
                                                            </Space>
                                                        </Space>
                                                    )}
                                                    {isInEmptyState ?
                                                        <Skeleton title={false} avatar paragraph={false} />
                                                    :   <>
                                                            {!this.props.releasePilotId && (
                                                                <Text type="secondary" style={{ fontStyle: 'italic' }}>
                                                                    No release pilot selected.
                                                                </Text>
                                                            )}
                                                        </>
                                                    }
                                                </Space>
                                            </div>
                                        )}
                                    </EmptyStateWrapper>
                                </div>
                                <div style={{ ...cardContainerStyle, paddingTop: 0 }}>
                                    <div style={{ ...cardStyle }}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    marginBottom: 16,
                                                }}
                                            >
                                                <Text style={{ ...moduleTitleStyle }} type="secondary">
                                                    Release description
                                                </Text>
                                                <PermissionedActionButton
                                                    actionType={RunwayUserAction.UAEditReleasePlanningSummary}
                                                    icon={<EditOutlined style={{ color: runwayColors.info }} />}
                                                    onClick={() =>
                                                        this.setState({
                                                            ...this.state,
                                                            versionSummaryModalVisible: true,
                                                        })
                                                    }
                                                    releaseId={this.props.releaseId}
                                                >
                                                    Edit
                                                </PermissionedActionButton>
                                            </div>
                                            <div style={{ flexBasis: 'calc(100% - 2em)' }}>
                                                <>
                                                    {this.props.planningSummary && (
                                                        <MarkdownParagraphWithEllipsis
                                                            content={this.props.planningSummary}
                                                            maxHeight="250px"
                                                            backgroundColor={antGrey.grey3}
                                                        />
                                                    )}

                                                    {!this.props.planningSummary && (
                                                        <Text style={{ fontStyle: 'italic' }} type="secondary">
                                                            No notes added.
                                                        </Text>
                                                    )}
                                                </>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Space>
                </Content>
                <EmptyStateWrapper
                    releaseStepType={this.props.releaseStepType}
                    appId={this.props.appId}
                    featureSections={[FeatureSection.KickoffCTAs]}
                    releaseId={this.props.releaseId}
                    shouldShowAsTooltip
                    customWrapperStyle={{ position: 'absolute' }}
                >
                    {(isInEmptyState) =>
                        (isInEmptyState || this.props.showFooter) && (
                            <Footer style={styles.footerStyle}>
                                <KickoffStepFooter
                                    {...this.props}
                                    workingBranch={this.props.workingBranch}
                                    shouldShowEmptyState={isInEmptyState}
                                />
                            </Footer>
                        )
                    }
                </EmptyStateWrapper>
                <PlanningSummaryModal
                    orgId={this.props.orgId}
                    appId={this.props.appId}
                    releaseId={this.props.releaseId}
                    open={this.state.versionSummaryModalVisible}
                    versionSummary={this.props.planningSummary}
                    displayableVersion={this.props.displayableVersion}
                    onCancel={() => this.setState({ ...this.state, versionSummaryModalVisible: false })}
                    onDidUpdate={() => this.setState({ ...this.state, versionSummaryModalVisible: false })}
                />
            </Layout>
        );
    }
}

StepContentKickOff.contextType = ReleaseStepInactiveContext;

const mapProps = (state: RootState, ownProps: ReleaseStepOrStepRunComponentProps) => {
    const { orgId, appId, releaseId, releaseStepType } = ownProps;

    const release = selectRelease(state, releaseId);
    const app = selectApp(state, appId);
    const orgIdParam = selectOrg(state, orgId).orgId;
    const releaseStep = getReleaseStepFromState(releaseStepType, release.id, state) as ReleaseStepKickoff;
    const releaseStatus = release.status;

    const deleteReleaseIsLoading = getIsLoading(TRY_DELETE_RELEASE_ENTITY, state, releaseId);
    const deleteReleaseSuccess = getUpdated(TRY_DELETE_RELEASE_ENTITY, state, releaseId);
    const deleteReleaseError = getError(TRY_DELETE_RELEASE_ENTITY, state, releaseId);

    const releaseType = release.type;
    const rollbackTargetVersionInfo = release.rollbackTargetVersionInfo;
    const appIdParam = app.appId;

    const editDeleteReleaseDisabled =
        release.status === ReleaseStatus.Skipped || release.status === ReleaseStatus.Completed;
    const skipReleaseDisabled = editDeleteReleaseDisabled || release.timelinePhase !== ReleaseTimelinePhase.Current;

    const version = release.version;
    const displayableVersion = release.displayableVersion;
    const releaseName = release.releaseName;

    const releasePilot = selectReleasePilotForRelease(state, appId, releaseId);
    const releasePilotId = release.releasePilotId;

    const releaseBranch = releaseStep.data.releaseBranch;
    const workingBranch = releaseStep.data.workingBranch;

    const isStaticReleaseBranch = releaseStep.data.isStaticReleaseBranch;
    const isTrunkBased = isStaticReleaseBranch && workingBranch === releaseBranch;

    const targetKickoffDate = release.targetKickoffDate;
    const targetReleaseDate = release.targetReleaseDate;

    const canBeSubmitted = releaseStep.data.canBeSubmitted;
    const canRevertCompletedStatus = release.canRevertCompletedStatus;

    const planningSummary = releaseStep.data.releasePlanningSummary;

    const showFooter = !!workingBranch;

    const isWorkflowsSSRStepsEnabled = selectIsWorkflowsSSRStepsEnabled(state, appId);

    return {
        orgId,
        appId,
        orgIdParam,
        appIdParam,
        releaseId,
        stepRunComponentProps: ownProps.stepRunComponentProps,
        releaseStepType,
        displayableVersion,
        releaseName,
        version,
        data: releaseStep.data,
        releaseType,
        releaseBranch,
        workingBranch,
        isStaticReleaseBranch,
        isTrunkBased,
        canBeSubmitted,
        canRevertCompletedStatus,
        releaseStatus,
        releasePilot,
        releasePilotId,
        targetKickoffDate,
        targetReleaseDate,
        planningSummary,
        showFooter,
        deleteReleaseIsLoading,
        deleteReleaseSuccess,
        deleteReleaseError,
        editDeleteReleaseDisabled,
        skipReleaseDisabled,
        rollbackTargetVersionInfo,
        isWorkflowsSSRStepsEnabled,
    };
};

const connector = connect(mapProps);

type Props = ConnectedProps<typeof connector>;

export default connector(StepContentKickOff);

const kickoffHelperText: (
    isStaticReleaseBranch: boolean,
    isTrunkBased: boolean,
    releaseBranch?: string,
    workingBranch?: string,
) => string = (isStaticReleaseBranch, isTrunkBased, releaseBranch, workingBranch) => {
    if (isStaticReleaseBranch) {
        return `Promote your working branch ${workingBranch ? `'${workingBranch}' ` : ''}to your release branch ${releaseBranch ? `'${releaseBranch}' ` : ''} to kick off your release.`;
    } else if (isTrunkBased) {
        return `Merge new commits to your ${workingBranch ? `'${workingBranch}' ` : 'working'} branch to kick off your release.`;
    } else {
        //default is release branch
        return 'Create a new release branch to kick off your release.';
    }
};
